import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const BOOKING_URL = process.env.REACT_APP_BOOKING_URL;

function BookingDatePicker({ className = '', promoCode, onBookClick }) {
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState(2);
  const [showCal, setShowCal] = useState(null); // 'checkin' | 'checkout' | null
  const [calMonth, setCalMonth] = useState(new Date());
  const checkinRef = useRef(null);
  const checkoutRef = useRef(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDate = (d) => d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const formatISO = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  // Close on outside click
  useEffect(() => {
    if (!showCal) return;
    const handleClick = (e) => {
      if (e.target.closest('[data-calendar-popup]') || e.target.closest('[data-testid="checkin-picker"]') || e.target.closest('[data-testid="checkout-picker"]')) return;
      setShowCal(null);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showCal]);

  const handleDateClick = (date) => {
    if (showCal === 'checkin') {
      setCheckIn(date);
      if (!checkOut || date >= checkOut) {
        const next = new Date(date);
        next.setDate(next.getDate() + 1);
        setCheckOut(next);
      }
      setShowCal('checkout');
      setCalMonth(new Date(date.getFullYear(), date.getMonth()));
    } else {
      if (date <= checkIn) return;
      setCheckOut(date);
      setShowCal(null);
    }
  };

  const handleBook = () => {
    // Fire analytics/ad tracking before navigating away
    try { if (typeof onBookClick === 'function') onBookClick(); } catch { /* ignore */ }
    // Use REACT_APP_BOOKING_URL with correct Siteminder parameters
    const baseUrl = BOOKING_URL;
    const params = new URLSearchParams();
    params.set('locale', 'en');
    params.set('items[0][adults]', String(guests));
    params.set('items[0][children]', '0');
    params.set('items[0][infants]', '0');
    params.set('currency', 'GBP');
    if (checkIn) params.set('checkInDate', formatISO(checkIn));
    if (checkOut) params.set('checkOutDate', formatISO(checkOut));
    if (promoCode) params.set('promoCode', promoCode);
    params.set('trackPage', 'no');
    window.open(`${baseUrl}?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const renderCalendar = () => {
    const year = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const adjustedFirst = firstDay === 0 ? 6 : firstDay - 1;

    const days = [];
    for (let i = 0; i < adjustedFirst; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));

    const isDisabled = (d) => !d || d < today || (showCal === 'checkout' && checkIn && d <= checkIn);
    const isSelected = (d) => d && ((checkIn && d.getTime() === checkIn.getTime()) || (checkOut && d.getTime() === checkOut.getTime()));
    const isInRange = (d) => d && checkIn && checkOut && d > checkIn && d < checkOut;

    return (
      <div data-calendar-popup className="bg-[#0F1419] border border-[#C9A961]/30 p-4 shadow-2xl w-[290px]" data-testid="booking-calendar">
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={(e) => { e.stopPropagation(); setCalMonth(new Date(year, month - 1)); }} className="p-1.5 text-[#C9A961] hover:bg-[#C9A961]/10 transition-colors cursor-pointer"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-serif text-[#E8E4DC]">{calMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <button type="button" onClick={(e) => { e.stopPropagation(); setCalMonth(new Date(year, month + 1)); }} className="p-1.5 text-[#C9A961] hover:bg-[#C9A961]/10 transition-colors cursor-pointer"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 mb-1">
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(d => (
            <div key={d} className="text-center text-[9px] text-[#C9A961]/50 py-1 tracking-wider uppercase">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {days.map((d, i) => (
            <button
              key={i} type="button" disabled={isDisabled(d)}
              onClick={(e) => { e.stopPropagation(); if (d && !isDisabled(d)) handleDateClick(d); }}
              className={`h-8 text-xs rounded-sm transition-all duration-150 ${
                !d ? '' :
                isDisabled(d) ? 'text-[#E8E4DC]/15 cursor-not-allowed' :
                isSelected(d) ? 'bg-[#C9A961] text-[#0F1419] font-bold' :
                isInRange(d) ? 'bg-[#C9A961]/15 text-[#C9A961]' :
                'text-[#E8E4DC]/70 hover:bg-[#C9A961]/10 hover:text-[#C9A961] cursor-pointer'
              }`}
            >
              {d?.getDate()}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-[#C9A961]/50 mt-2 text-center">
          {showCal === 'checkin' ? 'Select check-in date' : 'Select check-out date'}
        </p>
      </div>
    );
  };

  return (
    <div className={`${className}`} data-testid="booking-date-picker">
      <div className="flex flex-col sm:flex-row items-stretch gap-3">
        {/* Check-in */}
        <div className="relative flex-1" ref={checkinRef}>
          <button type="button" onClick={() => setShowCal(showCal === 'checkin' ? null : 'checkin')}
            className="w-full flex items-center gap-3 bg-[#0F1419]/80 border border-[#C9A961]/30 hover:border-[#C9A961] px-4 py-3 transition-colors text-left cursor-pointer"
            data-testid="checkin-picker"
          >
            <Calendar className="w-4 h-4 text-[#C9A961] shrink-0" strokeWidth={1.5} />
            <div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#C9A961]/60">Check-in</div>
              <div className="text-sm text-[#E8E4DC]">{checkIn ? formatDate(checkIn) : 'Select date'}</div>
            </div>
          </button>
        </div>

        {/* Check-out */}
        <div className="relative flex-1" ref={checkoutRef}>
          <button type="button" onClick={() => setShowCal(showCal === 'checkout' ? null : 'checkout')}
            className="w-full flex items-center gap-3 bg-[#0F1419]/80 border border-[#C9A961]/30 hover:border-[#C9A961] px-4 py-3 transition-colors text-left cursor-pointer"
            data-testid="checkout-picker"
          >
            <Calendar className="w-4 h-4 text-[#C9A961] shrink-0" strokeWidth={1.5} />
            <div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#C9A961]/60">Check-out</div>
              <div className="text-sm text-[#E8E4DC]">{checkOut ? formatDate(checkOut) : 'Select date'}</div>
            </div>
          </button>
        </div>

        {/* Guests */}
        <div className="flex-shrink-0 w-full sm:w-28">
          <div className="flex items-center gap-3 bg-[#0F1419]/80 border border-[#C9A961]/30 px-4 py-3 h-full">
            <div>
              <div className="text-[9px] tracking-[0.2em] uppercase text-[#C9A961]/60">Guests</div>
              <select value={guests} onChange={e => setGuests(Number(e.target.value))}
                className="bg-transparent text-sm text-[#E8E4DC] appearance-none outline-none cursor-pointer w-full"
                data-testid="guests-selector"
              >
                {[1,2,3,4].map(n => <option key={n} value={n} className="bg-[#0F1419]">{n} {n === 1 ? 'Guest' : 'Guests'}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* CTA */}
        <button type="button" onClick={handleBook}
          className="bg-[#C9A961] text-[#0F1419] px-6 sm:px-8 py-3 text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#D4B76E] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
          data-testid="booking-search-btn"
        >
          <span>Check Availability</span>
          <ChevronRight className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>

      {/* Calendar popup — modal overlay on all devices */}
      {showCal && (
        <div className="fixed inset-0 z-[100]" onClick={() => setShowCal(null)}>
          <div className="fixed inset-0 bg-black/40 sm:bg-black/20" />
          <div className="fixed inset-0 flex items-center justify-center sm:items-start sm:justify-center sm:pt-[280px]" onClick={() => setShowCal(null)}>
            <div onClick={e => e.stopPropagation()}>
              {renderCalendar()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingDatePicker;
